import { HttpClient } from '@adapters/HttpClient';
import {
  ExtraWorkOrder,
  ExtraWorkOrdersFilters,
  ExtraWorkOrdersPage,
  CreateExtraWorkOrderRequest,
  RegisterWasteRequest,
  CompleteLineRequest,
  PendingDispatchLocation,
  ExtraWorkCart,
} from '../models';
/**
 * Construye los parámetros advanced_filters para la API
 * Formato: advanced_filters[filters][0][column]=status, etc.
 */
function buildAdvancedFiltersParams(searchFilter: {
  filters: any[];
  logic: string;
}) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (
        f.value !== undefined &&
        f.value !== null &&
        !(Array.isArray(f.value) && f.value.length === 0)
      ) {
        // Para operadores 'in' y 'not_in', enviar como array
        if (f.operator === 'in' || f.operator === 'not_in') {
          const values = Array.isArray(f.value) ? f.value : [f.value];
          values.forEach((val, valIndex) => {
            params[`advanced_filters[filters][${i}][value][${valIndex}]`] = val;
          });
        } else {
          params[`advanced_filters[filters][${i}][value]`] = f.value;
        }
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

export class ExtraWorkOrdersApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Listar órdenes de trabajos extra con filtros y paginación
   * GET /extra-work-orders
   */
  async getExtraWorkOrders(
    filters: ExtraWorkOrdersFilters = {}
  ): Promise<ExtraWorkOrdersPage> {
    // 🎯 API REAL
    const {
      page = 1,
      page_size = 15,
      advanced_filters,
      sorting,
    } = filters;

    const params: Record<string, any> = {
      page,
      per_page: page_size,
    };

    // 🎯 Construir advanced_filters si existen
    if (advanced_filters) {
      const advancedFiltersParams = buildAdvancedFiltersParams(advanced_filters);
      Object.assign(params, advancedFiltersParams);
    }

    // 🎯 Agregar ordenamiento en formato: sorting[column]=...&sorting[direction]=...
    if (sorting?.column && sorting?.direction) {
      params['sorting[column]'] = sorting.column;
      params['sorting[direction]'] = sorting.direction;
    }

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/extra-work-orders',
      params,
      {},
      showGlobalLoading
    );

    // Mapear extra_work_order_lines a lines para mantener compatibilidad en cada orden
    if (response.data && Array.isArray(response.data)) {
      response.data = response.data.map((order: any) => {
        if (order.extra_work_order_lines && !order.lines) {
          order.lines = order.extra_work_order_lines.map((line: any) => {
            // Mapear location_id a done_location_id si es necesario
            if (line.location_id && !line.done_location_id) {
              line.done_location_id = line.location_id;
            }
            // Mapear location a done_location si es necesario
            if (line.location && !line.done_location) {
              line.done_location = line.location;
            }
            // Mapear extra_work_wastes a wastes dentro de cada línea
            if (line.extra_work_wastes && !line.wastes) {
              line.wastes = line.extra_work_wastes.map((waste: any) => {
                // Mapear images a evidences para mantener compatibilidad con el frontend
                if (waste.images && !waste.evidences) {
                  waste.evidences = waste.images.map((image: any) => ({
                    id: image.id,
                    waste_id: image.extra_work_waste_id || image.id,
                    file_path: image.file_path,
                    file_name: image.file_path?.split('/').pop() || `image_${image.id}`,
                    file_type: image.file_type || 'image/png',
                    file_size: image.file_size || 0,
                    created_at: image.created_at || new Date().toISOString(),
                  }));
                }
                // Mapear created_by si viene como user_id
                if (waste.user_id && !waste.created_by) {
                  waste.created_by = waste.user_id;
                }
                return waste;
              });
            }
            return line;
          });
        }
        return order;
      });
    }

    return response as ExtraWorkOrdersPage;
  }

  /**
   * Obtener detalle de una orden
   * GET /extra-work-orders/{id}
   */
  async getExtraWorkOrderById(id: number): Promise<ExtraWorkOrder> {
    // 🎯 API REAL
    const response = await this.httpClient.get<any>(
      `/extra-work-orders/${id}`
    );
    
    // Mapear extra_work_order_lines a lines para mantener compatibilidad
    if (response.extra_work_order_lines) {
      // Intentar extraer receipt_id si falta (merchandise_id)
      if (!response.merchandise_id && response.notes) {
        const receiptMatch = response.notes.match(/albaran\s+de\s+entrada\s+ID:\s*(\d+)/i) || 
                            response.notes.match(/ID:\s*(\d+)/i) ||
                            response.ref.match(/ENTRY-(\d+)/i);
        if (receiptMatch) {
          response.merchandise_id = parseInt(receiptMatch[1], 10);
        }
      }

      // Siempre mapear, incluso si ya existe lines (para asegurar que wastes se mapee)
      response.lines = response.extra_work_order_lines.map((line: any) => {
        // Mapear location_id a done_location_id si es necesario
        if (line.location_id && !line.done_location_id) {
          line.done_location_id = line.location_id;
        }
        // Mapear location a done_location si es necesario
        if (line.location && !line.done_location) {
          line.done_location = line.location;
        }
        
        // Mapear extra_work_wastes a wastes dentro de cada línea
        if (line.extra_work_wastes && !line.wastes) {
          line.wastes = line.extra_work_wastes.map((waste: any) => {
            // Mapear images a evidences para mantener compatibilidad con el frontend
            if (waste.images && !waste.evidences) {
              waste.evidences = waste.images.map((image: any) => ({
                id: image.id,
                waste_id: image.extra_work_waste_id || image.id, // Mapear extra_work_waste_id a waste_id
                file_path: image.file_path,
                file_name: image.file_path?.split('/').pop() || `image_${image.id}`,
                file_type: image.file_type || 'image/png',
                file_size: image.file_size || 0,
                created_at: image.created_at || new Date().toISOString(),
              }));
            }
            // Mapear created_by si viene como user_id
            if (waste.user_id && !waste.created_by) {
              waste.created_by = waste.user_id;
            }
            return waste;
          });
        }
        return line;
      });
    }
    
    return response as ExtraWorkOrder;
  }

  /**
   * Crear nueva orden de trabajo extra
   * POST /extra-work-orders
   */
  async createExtraWorkOrder(
    data: CreateExtraWorkOrderRequest
  ): Promise<ExtraWorkOrder> {
    const response = await this.httpClient.post<ExtraWorkOrder>(
      '/extra-work-orders',
      data
    );
    return response;
  }

  /**
   * Iniciar una línea de trabajo
   * POST /extra-work-orders/{id}/lines/{line_id}/started
   */
  async startLine(
    orderId: number,
    lineId: number
  ): Promise<{ message: string; success: boolean }> {
    const response = await this.httpClient.post<{
      message: string;
      success: boolean;
    }>(`/extra-work-orders/${orderId}/lines/${lineId}/started`, {});
    return response;
  }

  /**
   * Completar una línea de trabajo
   * POST /extra-work-orders/{id}/lines/{line_id}/completed
   */
  async completeLine(
    orderId: number,
    lineId: number,
    data: CompleteLineRequest
  ): Promise<{ message: string; success: boolean }> {
    // 🎯 API REAL
    const response = await this.httpClient.post<{
      message: string;
      success: boolean;
    }>(`/extra-work-orders/${orderId}/lines/${lineId}/completed`, data);
    return response;
  }

  /**
   * Registrar merma en una línea
   * POST /extra-work-orders/{id}/lines/{line_id}/waste
   */
  async registerWaste(
    orderId: number,
    lineId: number,
    data: RegisterWasteRequest
  ): Promise<{ message: string; success: boolean }> {
    // 🎯 API REAL
    // Crear FormData para enviar archivos
    const formData = new FormData();
    formData.append('qty_wasted', String(data.qty));
    if (data.description) {
      formData.append('description', data.description);
    }

    // Agregar evidencias (archivos) - La API espera images_paths[]
    data.evidences.forEach((file) => {
      formData.append('images_paths[]', file);
    });

    const response = await this.httpClient.post<{
      message: string;
      success: boolean;
    }>(
      `/extra-work-orders/${orderId}/lines/${lineId}/waste`,
      formData,
      true,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    return response;
  }

  /**
   * Obtener ubicaciones (carritos) pendientes de despacho para una orden
   * GET /extra-work-orders/{id}/pending-dispatch-locations
   */
  async getPendingDispatchLocations(orderId: number): Promise<PendingDispatchLocation[]> {
    const response = await this.httpClient.get<PendingDispatchLocation[]>(
      `/extra-work-orders/${orderId}/pending-dispatch-locations`
    );
    return response;
  }

  /**
   * Obtener estadísticas de carritos asociados a una orden
   * GET /extra-work-orders/{id}/carts
   */
  async getCarts(orderId: number): Promise<{ data: ExtraWorkCart[] }> {
    const response = await this.httpClient.get<{ data: ExtraWorkCart[] }>(
      `/extra-work-orders/${orderId}/carts`
    );
    return response;
  }

  /**
   * Despachar una ubicación
   * POST /extra-work-orders/{id}/dispatch-by-location
   * El cuerpo debe ser {"location_id": Integer}
   */
  async dispatchByLocation(orderId: number, data: { location_id: number }): Promise<void> {
    const url = `/extra-work-orders/${orderId}/dispatch-by-location`;
    
    // Asegurar que solo enviamos location_id y que sea un texto (string)
    // Nota: Aunque la documentación diga Integer, la API devuelve error 422 si no es string.
    const payload = {
      location_id: String(data.location_id)
    };

    try {
      await this.httpClient.post(url, payload);
    } catch (error) {
      throw error;
    }
  }
}
